import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { CreditCard, Check, Loader, Zap, ShieldCheck, Gift, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const Billing = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const packages = [
    { id: 'starter', name: 'Starter', credits: 100, price: 9.99, per_credit: 0.100, popular: false },
    { id: 'pro', name: 'Pro', credits: 500, price: 39.99, per_credit: 0.080, popular: true },
    { id: 'business', name: 'Business', credits: 1000, price: 74.99, per_credit: 0.075, popular: false },
    { id: 'enterprise', name: 'Enterprise', credits: 5000, price: 299.99, per_credit: 0.060, popular: false },
  ];

  const txt = {
    en: {
      title: 'Billing & Credits',
      balance: 'AVAILABLE CREDITS',
      purchase: 'Purchase Credits',
      purchaseSub: 'Top up your account with AI credits',
      perCredit: 'per credit',
      neverExpires: 'Never expires',
      allTools: 'All AI tools included',
      payMethods: 'iDEAL, Creditcard, Google Pay & Link',
      buyBtn: 'Purchase',
      processing: 'Processing...',
      history: 'Transaction History',
      noTx: 'No transactions yet.',
      date: 'Date',
      package: 'Package',
      credits: 'Credits',
      amount: 'Amount',
      payStatus: 'Status',
      accepted: 'Accepted payment methods',
      paySuccess: 'Payment successful! Credits have been added.',
      bonusMsg: 'You received 25 free credits to get started!',
      fillProfile: 'Fill in your company profile to activate Emma',
      goToProfile: 'Fill in company profile',
    },
    nl: {
      title: 'Facturatie & Credits',
      balance: 'BESCHIKBARE CREDITS',
      purchase: 'Credits Kopen',
      purchaseSub: 'Vul je account aan met AI credits',
      perCredit: 'per credit',
      neverExpires: 'Verloopt nooit',
      allTools: 'Alle AI tools inbegrepen',
      payMethods: 'iDEAL, Creditcard, Google Pay & Link',
      buyBtn: 'Kopen',
      processing: 'Verwerken...',
      history: 'Transactiegeschiedenis',
      noTx: 'Nog geen transacties.',
      date: 'Datum',
      package: 'Pakket',
      credits: 'Credits',
      amount: 'Bedrag',
      payStatus: 'Status',
      accepted: 'Geaccepteerde betaalmethoden',
      paySuccess: 'Betaling geslaagd! Credits zijn toegevoegd.',
      bonusMsg: 'Je hebt 25 gratis credits ontvangen om te starten!',
      fillProfile: 'Vul je bedrijfsprofiel in om Emma te activeren',
      goToProfile: 'Bedrijfsprofiel invullen',
    },
  };
  const t = txt[lang] || txt.nl;

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollCheckoutStatus(sessionId);
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${API}/billing/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalance(res.data.credits || 0);
    } catch {}
    setLoading(false);
  };

  const fetchTransactions = async () => {
    try {
      const res = await axios.get(`${API}/billing/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(res.data.transactions || []);
    } catch {}
  };

  const purchaseCredits = async (packageId) => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/billing/purchase-credits`, {
        package_id: packageId,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (e) {
      alert(lang === 'nl' ? 'Er ging iets mis. Probeer opnieuw.' : 'Something went wrong.');
      setPurchasing(false);
    }
  };

  const pollCheckoutStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.payment_status === 'paid') {
          setPaymentSuccess(true);
          fetchBalance();
          fetchTransactions();
          // Remove session_id from URL
          window.history.replaceState({}, '', '/portal/billing');
          return;
        }
        if (res.data.status === 'expired') return;
      } catch {}
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      }
    };
    poll();
  };

  if (loading) return <div className="loading-screen"><div className="loader"></div></div>;

  return (
    <div className="tool-page" data-testid="billing-page">
      <div className="tool-header">
        <h1 className="heading-1">{t.title}</h1>
      </div>

      <div className="billing-container">

        {/* Payment Success Banner */}
        {paymentSuccess && (
          <div className="billing-success-banner" data-testid="payment-success">
            <Check size={20} />
            <span>{t.paySuccess}</span>
            <button onClick={() => navigate('/portal/company-profile')} className="btn-primary" style={{ marginLeft: 'auto', padding: '0.4rem 1rem', fontSize: '0.85rem' }}>
              {t.goToProfile} <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Balance Card */}
        <div className="subscription-card" data-testid="balance-card">
          <div className="subscription-header">
            <div>
              <p className="caption">{t.balance}</p>
              <h1 className="heading-hero" data-testid="credits-remaining">{balance}</h1>
            </div>
            {balance <= 25 && balance > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent)', fontSize: '0.85rem' }}>
                <Gift size={16} />
                <span>{t.bonusMsg}</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment methods */}
        <div className="billing-payment-methods" data-testid="payment-methods">
          <ShieldCheck size={16} />
          <span>{t.accepted}:</span>
          <span className="pay-badge">Creditcard</span>
          <span className="pay-badge pay-badge-ideal">iDEAL</span>
          <span className="pay-badge">Google Pay</span>
          <span className="pay-badge">Link</span>
        </div>

        {/* Packages */}
        <div className="billing-section">
          <h2 className="heading-2">{t.purchase}</h2>
          <p className="body-medium text-secondary">{t.purchaseSub}</p>

          <div className="packages-grid">
            {(Array.isArray(packages) ? packages : []).map((pkg) => (
              <div key={pkg.id} className={`package-card ${pkg.popular ? 'popular' : ''}`} data-testid={`pkg-${pkg.id}`}>
                {pkg.popular && <div className="popular-badge">{lang === 'nl' ? 'POPULAIRST' : 'MOST POPULAR'}</div>}
                <h3 className="heading-2">{pkg.name}</h3>
                <div className="package-price">
                  <span className="price-amount">&euro;{pkg.price.toFixed(2)}</span>
                </div>
                <p className="package-credits">{pkg.credits} Credits</p>
                <ul className="package-features">
                  <li><Check size={16} /> &euro;{pkg.per_credit.toFixed(3)} {t.perCredit}</li>
                  <li><Check size={16} /> {t.neverExpires}</li>
                  <li><Check size={16} /> {t.allTools}</li>
                  <li><Zap size={16} /> {t.payMethods}</li>
                </ul>
                <button
                  onClick={() => purchaseCredits(pkg.id)}
                  disabled={purchasing}
                  className="btn-primary"
                  style={{ width: '100%' }}
                  data-testid={`buy-${pkg.id}`}
                >
                  <CreditCard size={16} />
                  {purchasing ? <><Loader size={14} className="spin" /> {t.processing}</> : t.buyBtn}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Transaction History */}
        <div className="billing-section">
          <h2 className="heading-2">{t.history}</h2>
          {transactions.length === 0 ? (
            <p className="body-medium text-secondary">{t.noTx}</p>
          ) : (
            <div className="transactions-table">
              <table>
                <thead>
                  <tr>
                    <th>{t.date}</th>
                    <th>{t.package}</th>
                    <th>{t.credits}</th>
                    <th>{t.amount}</th>
                    <th>{t.payStatus}</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(transactions) ? transactions : []).map((tx, i) => (
                    <tr key={tx.session_id || i}>
                      <td>{new Date(tx.created_at).toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-US')}</td>
                      <td>{tx.package_name || tx.package_id}</td>
                      <td>{tx.credits}</td>
                      <td>&euro;{tx.amount?.toFixed(2)}</td>
                      <td>
                        <span className={`status-badge ${tx.payment_status}`}>
                          {tx.payment_status === 'paid' ? (lang === 'nl' ? 'Betaald' : 'Paid') :
                           tx.payment_status === 'pending' ? (lang === 'nl' ? 'In behandeling' : 'Pending') :
                           tx.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
